import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Eye, WarningOctagon, CaretRight, CaretDown, CheckCircle, ArrowsClockwise,
} from "@phosphor-icons/react";
import { authClient } from "../lib/authApi";
import { errorMessage } from "../lib/errorMessage";
import { toast } from "./ui/sonner";
import { BlockingMessageDialog } from "./BlockingMessageDialog";
import { fmtMoney } from "../lib/format";
import { useWorkflowOptional } from "../workflow/WorkflowContext";
import { AddRegistrationCta } from "./AddRegistrationCta";
import { ROWS_PAGE, addPage, withMoreRows } from "./review_queue/moreRows";   // A1-105
import { cardIntentFor, clearCardIntent } from "../workflow/cardIntent";   // A2-581 lead 6
import { takeCalcOutcomeNotice } from "../workflow/calcOutcomeNotice";   // A2-581 lead 4
import { PerRowResolver } from "./review_queue/PerRowResolver";
import { ServiceCountryResolver } from "./review_queue/ServiceCountryResolver";
import { RefundChannelResolver } from "./review_queue/RefundChannelResolver";
import { RefundAuditView } from "./review_queue/RefundAuditView";
import { MissingRateAction } from "./review_queue/MissingRateAction";

// v0.5.0f+4 — server-generated default reason text for bulk-ack.
// Replaces the v0.5.0c ReasonPicker + preset-dropdown + <TOKEN>
// placeholder pattern that blocked the ACKNOWLEDGE ALL button
// until the operator hand-edited tokens like `<DATE>`. Same fix
// pattern as the v0.5.0f Inspect drawer redesign: generate the
// full reason text from the group's actual data, render it read-
// only by default, expose an "Edit" toggle for power users.
import { ReviewGroupCard } from "./review_queue/ReviewGroupCard";
import { buildCanonicalMap } from "./review_queue/reviewCanonical";
import { ToFileList } from "./review_queue/ToFileList";   // A1-100
import { isSignedOff } from "../workflow/calcStatus";   // A2-581 lead 35
export const GroupedReviewQueue = ({ calcId, highlightSection = null, onInspect = null }) => {
  const { t } = useTranslation();
  const wf = useWorkflowOptional();
  const clientId = wf?.clientId || null;
  // A2-526 (operator 2026-10-25: after a re-run Step 4 still showed the old
  // calculation's cards). The cards are kept WITH the calc they came from and
  // shown only while that calc is the one on screen; a reply for any calc but
  // the latest one asked for is dropped. Before, a new calc kept the old cards
  // until its own arrived (1-3 s on a large file), for good if that fetch
  // failed, and a slow reply for the old calc could land after the new one.
  // A re-run from a card already fetched the new calc's review with the calc
  // itself (WorkflowContext.runCalc), so that is shown at once: the cards
  // never blank out between an answer and its re-run (independent review).
  const [loaded, setLoaded] = useState({ cid: null, data: null });
  const base = (loaded.cid && loaded.cid === calcId && loaded.data)
    || (calcId && wf?.calc?.id === calcId && wf?.review) || null;
  // A1-105 (review 2026-11-01: the screen loaded only the first 50 rows of a
  // card, with no way to the rest): a card's later pages, kept against the
  // reply they extend, so any refresh (a new reply) starts from page 1 again.
  const [more, setMore] = useState({ base: null, byFlag: {} });
  const data = useMemo(() => withMoreRows(base, more.base === base ? more.byFlag : {}), [base, more]);
  const baseRef = useRef(base);
  baseRef.current = base;
  // the pages each card had open: an answer refreshes the reply, and the card
  // comes back with the same rows instead of the first 50 (review 2026-11-01)
  const wanted = useRef({});
  const fetchPage = useCallback(async (flag, page, forBase) => {
    const { data: reply } = await authClient.get(`/runs/calc/${calcId}/review-grouped`,
      { params: { page, page_size: ROWS_PAGE, flag_type: flag } });
    const next = (reply?.groups || []).find((x) => x.flag_type === flag);
    if (!next) return false;                             // the card is gone from this reply
    if (baseRef.current !== forBase) return true;        // a newer reply came in: this page is stale
    setMore((prev) => {
      const byFlag = prev.base === forBase ? prev.byFlag : {};
      return { base: forBase, byFlag: { ...byFlag, [flag]: addPage(byFlag[flag], next, page) } };
    });
    return true;
  }, [calcId]);
  const moreRows = useCallback(async (flag) => {
    if (!calcId || !base) return;
    const had = more.base === base ? more.byFlag[flag] : null;
    const page = (had?.pages || 1) + 1;
    // remembered before the reply, for this calculation only (review 2026-11-01)
    wanted.current = { ...wanted.current, [flag]: Math.max(page, wanted.current[flag] || 0) };
    await fetchPage(flag, page, base);
  }, [calcId, base, more, fetchPage]);
  useEffect(() => { wanted.current = {}; }, [calcId]);   // another calculation: its cards start at page 1
  useEffect(() => {   // a new reply: fetch again the pages each card had open
    if (!base || !calcId) return;
    const pagesOpen = Object.entries(wanted.current);
    (async () => {
      for (const [flag, pages] of pagesOpen) {
        for (let p = 2; p <= pages && baseRef.current === base; p += 1) {
          let still = true;
          try { still = await fetchPage(flag, p, base); } catch { break; }
          if (!still) {   // the card is gone (all answered): stop asking for it
            const rest = { ...wanted.current };
            delete rest[flag];
            wanted.current = rest;
            break;
          }
        }
      }
    })();
  }, [base, calcId, fetchPage]);
  const latestAsked = useRef(null);
  const [open, setOpen] = useState({});
  const [draftReason, setDraftReason] = useState({});
  const [busy, setBusy] = useState({});
  const [hidden, setHidden] = useState(false);
  // A load error belongs to the calc it happened on (it must not open over
  // the next calc's review).
  const [loadError, setLoadError] = useState({ cid: null, msg: null, dismissed: false });
  const fetchError = loadError.cid === calcId && !loadError.dismissed ? loadError.msg : null;
  const errorDismissed = loadError.cid === calcId && loadError.dismissed;
  const setFetchError = (msg) => setLoadError(msg ? { cid: latestAsked.current, msg, dismissed: false }
                                                   : { cid: null, msg: null, dismissed: false });
  const [ackError, setAckError] = useState(null);

  // v0.6.7 — accepts an optional override id. A per-row resolve that
  // triggers a recalc supersedes `calcId` (this component's own prop,
  // closed over below) before this function's caller gets a chance to
  // react to the new prop via the effect further down — passing the
  // fresh id explicitly avoids fetching the now-superseded calc's data.
  const refresh = useCallback(async (overrideCalcId) => {
    const cid = overrideCalcId || calcId;
    latestAsked.current = cid;
    setLoadError({ cid: null, msg: null, dismissed: false });
    try {
      const { data } = await authClient.get(
        `/runs/calc/${cid}/review-grouped`,
        { params: { page: 1, page_size: 50 } }
      );
      if (latestAsked.current !== cid) return;   // A2-526: an older calc's reply
      setLoaded({ cid, data });
      // A2-579: nothing opens by itself — the to-file list opens a group.
      setOpen((prev) => ({ ...prev }));
    } catch (e) {
      if (latestAsked.current !== cid) return;
      if (e?.response?.status === 403) setHidden(true);
      else {
        // v0.6.8 — was toast.error(); when this fetch fails the
        // component renders NOTHING (`!data` → null below), so the
        // toast used to be the ONLY signal — missing it looked
        // identical to "no review needed."
        setFetchError(errorMessage(e));
      }
    }
  }, [calcId]);

  useEffect(() => {
    if (calcId) refresh();
  }, [calcId, refresh]);
  // A2-581 lead 10: a decision taken outside the cards (the VAT-number
  // queue) changes what they show.
  useEffect(() => {
    const onUpdated = (e) => { if (calcId && e?.detail?.calcId === calcId) refresh(); };
    window.addEventListener("review-updated", onUpdated);
    return () => window.removeEventListener("review-updated", onUpdated);
  }, [calcId, refresh]);

  // v0.4.22 F3 — when the operator clicks a section's "⚠ N flags"
  // badge in FilingSections, this prop flips to the section ID. We
  // (1) auto-expand every group whose section_counts contains that
  // section and (2) highlight those groups with a teal ring so the
  // operator's eye lands instantly.
  useEffect(() => {
    if (!highlightSection || !data) return;
    const next = {};
    for (const g of data.groups || []) {
      if ((g.section_counts || {})[highlightSection] > 0) {
        next[g.flag_type] = true;
      }
    }
    if (Object.keys(next).length > 0) {
      setOpen((s) => ({ ...s, ...next }));
    }
  }, [highlightSection, data]);

  // A2-523 — a country form's "Pick the right rate" links here with
  // ?card=<flag>: open that card and bring it into view. Read from the
  // address directly so the queue still renders outside a router (tests).
  // A2-581 lead 6: a link naming its calculation asks for the card through
  // workflow/cardIntent, answered only on that calculation; an older link
  // with a bare ?card= opens it on the calculation shown. Once, not again on
  // every refresh.
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const intended = cardIntentFor(calcId);
  const wantCard = intended || (urlParams && !urlParams.get("calc_id") ? urlParams.get("card") : null);
  const cardOpened = useRef(null);
  // Open a card and bring it into view (the ?card= link, and A1-100's list).
  const [selectedCard, setSelectedCard] = useState(null);   // A2-598: shown selected in the to-file list
  // Operator 2026-11-02: one card at a time ("when you click on two or more,
  // it's easy to get lost"): opening a card closes the one open before.
  const openCard = (flag) => {
    setSelectedCard(flag);
    setOpen({ [flag]: true });
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="rg-group-${flag}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };
  useEffect(() => {
    if (!wantCard || !(data?.groups || []).some((g) => g.flag_type === wantCard)) return;
    if (cardOpened.current === `${calcId}:${wantCard}`) return;
    cardOpened.current = `${calcId}:${wantCard}`;
    if (intended) clearCardIntent();
    openCard(wantCard);
  }, [wantCard, data]);   // eslint-disable-line react-hooks/exhaustive-deps

  // v0.4.98 — read calc.frozen from workflow context. When the
  // calc is frozen the audit trail is locked, so bulk-ack writes
  // would 409 server-side. Pre-emptively disable + show a clear
  // banner instead of letting the operator click into silence.
  // Operator 2026-10-18: "Block edits after sign-off" — a signed-off calc
  // is locked for decisions too (the server refuses, api/_signoff_guard).
  const calcSignedOff = !wf?.calc?.frozen && isSignedOff(wf?.calc);
  // A2-581 lead 31: a calc a newer run replaced takes no answers either
  // (the server refuses them, api/_signoff_guard CALC_REPLACED).
  const calcReplaced = !wf?.calc?.frozen && !calcSignedOff && !!wf?.calc?.superseded_by_calc_id;
  const calcFrozen = !!wf?.calc?.frozen || calcSignedOff || calcReplaced;

  const fetchErrorDialog = (
    <BlockingMessageDialog
      open={!!fetchError} testid="review-queue-fetch-error" severity="error"
      title={t("ui.review_grouped.load_failed_title", { defaultValue: "Couldn't load the review queue" })} onOpenChange={(o) => !o && setLoadError((e) => ({ ...e, dismissed: true }))}
    ><p>{fetchError}</p></BlockingMessageDialog>
  );
  // A2-581 lead 4: the run's "filing is blocked" message, shown here once
  // (its own panel unmounted when the run moved to Review)
  const [outcomeNotice, setOutcomeNotice] = useState(null);
  useEffect(() => {
    const n = takeCalcOutcomeNotice(calcId);
    if (n) setOutcomeNotice(n);
  }, [calcId]);
  const outcomeDialog = (
    <BlockingMessageDialog
      open={!!outcomeNotice} testid="review-queue-calc-outcome" severity="error"
      title={outcomeNotice?.title || ""} onOpenChange={(o) => !o && setOutcomeNotice(null)}
    ><p>{outcomeNotice?.body}</p></BlockingMessageDialog>
  );
  const ackErrorDialog = (
    <BlockingMessageDialog
      open={!!ackError} testid="review-queue-ack-error" severity="error"
      title={t("ui.review_grouped.ack_failed_title", { defaultValue: "Couldn't acknowledge" })} onOpenChange={(o) => !o && setAckError(null)}
    ><p>{ackError}</p></BlockingMessageDialog>
  );

  if (hidden || !calcId) return null;
  if (!data) {
    return (
      <>
        {errorDismissed ? (
          <button type="button" data-testid="review-queue-retry" onClick={() => refresh()}
                  className="text-xs underline text-slate-700 dark:text-slate-200 px-3 py-2">
            {t("ui.review_grouped.retry_load", { defaultValue: "The review did not load. Try again" })}
          </button>
        ) : !fetchError && (
          <p role="status" aria-live="polite" data-testid="review-queue-loading"
             className="text-xs text-slate-600 dark:text-slate-300 px-3 py-2">
            {t("ui.review_grouped.loading_this_calc", { defaultValue: "Loading the review for this calculation…" })}
          </p>
        )}
        {fetchErrorDialog}
        {outcomeDialog}
      </>
    );
  }

  // v0.4.98 — translate the specific 409 backend codes operators
  // hit on this surface. Falls back to the generic v0.4.96
  // errorMessage helper for anything else so we never render a
  // raw {error: ...} object.
  const translateAckError = (e) => {
    const detail = e?.response?.data?.detail;
    if (detail && typeof detail === "object" && detail.error) {
      const code = detail.error;
      if (code === "CALC_FROZEN") {
        return t("ui.review_grouped.err_calc_frozen", {
          defaultValue:
            "This calc is frozen and immutable — resolving or acknowledging "
            + "rows is locked. To make changes, run a new calc and supersede "
            + "this one.",
        });
      }
      if (code === "NO_TXNS_MATCH_FLAG") {
        return t("ui.review_grouped.err_no_txns", {
          defaultValue:
            "No matching transactions left to acknowledge — they may have "
            + "been resolved on another tab. Refresh the page to pick up the "
            + "latest review queue.",
        });
      }
      if (code === "FLAG_NOT_BULK_ACKABLE") {
        return t("ui.review_grouped.err_not_bulk_ackable", {
          defaultValue:
            "This flag type requires a per-row decision — bulk acknowledge "
            + "isn't allowed for it.",
        });
      }
    }
    return errorMessage(e);
  };

  const ackGroup = async (group, explicitReason) => {
    if (calcFrozen) return; // belt-and-braces — button is disabled
    // v0.5.0f+4 — caller passes the resolved reason explicitly
    // (the auto-generated default OR the operator-edited variant).
    // Avoids the React stale-state closure bug from v0.5.0f+1.
    const reason = (explicitReason
      ?? draftReason[group.flag_type] ?? "").trim();
    if (!reason || reason.length < 3 || /[<>]/.test(reason)) {
      toast.error(t("ui.review_grouped.reason_required"));
      return;
    }
    setBusy((s) => ({ ...s, [group.flag_type]: true }));
    try {
      await authClient.post(`/runs/calc/${calcId}/review-acknowledge`, {
        flag_type: group.flag_type,
        reason,
      });
      setDraftReason((s) => ({ ...s, [group.flag_type]: undefined }));
      await refresh();
      await wf?.refreshReview?.(calcId);
    } catch (e) {
      // v0.6.8 — was toast.error(); these are actionable, must-read
      // explanations (e.g. "run a new calc to supersede this one"),
      // easy to skim past in an auto-dismissing toast.
      setAckError(translateAckError(e));
    } finally {
      setBusy((s) => ({ ...s, [group.flag_type]: false }));
    }
  };

  const totals = data;

  // v0.5.6 UX Phase 1 (item 5) — three-state hierarchy derived on the
  // client from the group totals (count / acknowledged_count are full
  // group totals, not paged). Blocking = Family-B rows awaiting a
  // per-row decision (these hold up sign-off); needs-decision =
  // Family-A rows awaiting a bulk review note.
  const groupsAll = data.groups || [];
  // A2-637 Owner C: a card the server marks as stopping filing (a duplicates
  // card holding an open exact copy) is blocking here too
  const blockingRows = groupsAll
    .filter((g) => g.family === "B" || g.tier === "must")
    .reduce((a, g) => a + (g.count - (g.acknowledged_count || 0)), 0);
  const needsRows = groupsAll
    .filter((g) => g.family === "A" && g.tier !== "info" && g.tier !== "must")   // A1-103: notes are not to review
    .reduce((a, g) => a + (g.count - (g.acknowledged_count || 0)), 0);

  // v0.6 — canonical-row map (presentation dedup). Rebuilt EVERY render from
  // the loaded groups so a resolved flag auto-promotes the next card.
  const canonicalMap = buildCanonicalMap(groupsAll);

  // v0.5.6 — group status: "blocking" | "needs-decision" | "reviewed".
  return (
    <>
    <section data-testid="grouped-review-queue" className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <header className="border-b border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center justify-between">
        <div className="text-xs tracking-normal text-slate-500 dark:text-slate-400 tabular-nums flex items-center gap-2">
          <Eye size={14} /> {t("ui.review_grouped.title")}
        </div>
        <div className="text-[11px] flex items-center gap-2">
          <span
            data-testid="rg-blocking-count"
            className={`flex items-center gap-1 tabular-nums ${
              blockingRows > 0 ? "text-orange-700 dark:text-orange-300 font-semibold" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            <WarningOctagon size={12} weight="bold" />
            {t("ui.review_grouped.blocking_chip", {
              count: blockingRows,
              defaultValue: "{{count}} blocking",
            })}
          </span>
          <span
            data-testid="rg-needs-decision-count"
            className="flex items-center gap-1 tabular-nums text-slate-500 dark:text-slate-400"
          >
            {t("ui.review_grouped.needs_decision_chip", {
              count: needsRows,
              defaultValue: "{{count}} to review",
            })}
          </span>
          {totals.replayed_count > 0 && (
            <span className="tabular-nums text-teal-700 dark:text-teal-300 px-2 py-0.5" data-testid="rg-replayed-count">
              {t("ui.review_grouped.replayed_chip", { count: totals.replayed_count })}
            </span>
          )}
        </div>
      </header>

      {/* v0.4.98 — frozen-calc lock banner. Without it the
          ACKNOWLEDGE ALL buttons would all 409 silently — operator
          would click into the void and not understand why. */}
      {calcSignedOff && (
        <div
          data-testid="rg-signed-off-banner"
          className="px-4 py-2 text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-300 dark:border-amber-700"
        >
          🔒 {t("ui.review_grouped.signed_off_banner", { defaultValue: "This calculation is signed off, so its decisions are fixed. To change anything, run the calculation again (Step 3) and sign off the new run." })}
        </div>
      )}
      {calcReplaced && (
        <div
          data-testid="rg-replaced-banner"
          className="px-4 py-2 text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-300 dark:border-amber-700"
        >
          🔒 {t("ui.review_grouped.replaced_banner", { defaultValue: "A newer calculation has replaced this one, so answers given here would not reach the return. Open the current calculation (top of the page) and answer there." })}
        </div>
      )}
      {calcFrozen && !calcSignedOff && !calcReplaced && (
        <div
          data-testid="rg-frozen-banner"
          className="px-4 py-2 tabular-nums text-[11px] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-300 dark:border-amber-700"
        >
          🔒 {t("ui.review_grouped.frozen_banner", { defaultValue: "This calc is frozen and immutable — its filing reference is locked. Resolving or acknowledging rows here is disabled. To make changes, run a new calc and supersede this one." })}
        </div>
      )}

      {/* A1-105 (the card redesign's finish line): nothing left to answer or
          check, one banner and the way on, instead of a page of folded cards */}
      {/* A2-581 screen map lead 11: only when Step 4 itself is complete (a
          stale calc is not), so the way on never lands on a refused sign-off */}
      {groupsAll.length > 0 && !calcFrozen && blockingRows === 0 && needsRows === 0
        && (!wf?.state?.steps || wf.state.steps[4]?.complete) && (
        <div data-testid="rg-all-done" role="status"
          className="px-4 py-2 text-xs flex items-center gap-3 text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950/40 border-b border-emerald-200 dark:border-emerald-800">
          <span>{t("ui.review_grouped.all_done", { defaultValue: "All review items done." })}</span>
          {wf?.setActiveStep && (
            <button type="button" data-testid="rg-all-done-signoff" onClick={() => wf.setActiveStep(5)}
              className="font-semibold underline underline-offset-2">
              {t("ui.review_grouped.all_done_cta", { defaultValue: "Go to sign-off →" })}
            </button>
          )}
        </div>
      )}
      {groupsAll.length > 0 && !calcFrozen && (
        <div className="px-4 pt-3">
          <ToFileList groups={groupsAll} onOpen={openCard} clientId={clientId} selected={selectedCard}
                      waitingElsewhere={data.waiting_elsewhere} />
        </div>
      )}

      {groupsAll.length === 0 ? (
        <div className="px-4 py-3 tabular-nums text-xs text-slate-500 dark:text-slate-400">
          {t("ui.review_grouped.empty")}
        </div>
      ) : (
        <ul className="divide-y divide-slate-800/60">
          {/* A2-579: the list is the page; a group shows once opened from it
              (every card, folded, on a frozen or signed-off calc — no list there). */}
          {groupsAll.filter((g) => calcFrozen || open[g.flag_type]).map((g) => (
            <ReviewGroupCard
              key={g.flag_type}
              g={g} calcId={calcId} clientId={clientId}
              calcFrozen={calcFrozen} highlightSection={highlightSection}
              onInspect={onInspect}
              open={open} setOpen={setOpen}
              draftReason={draftReason} setDraftReason={setDraftReason}
              busy={busy} ackGroup={ackGroup}
              refresh={refresh} wf={wf} data={data} onMoreRows={moreRows}
              canonicalMap={canonicalMap}
            />
          ))}
        </ul>
      )}
    </section>
    {ackErrorDialog}
    {outcomeDialog}
    </>
  );
};
